/**
 * Shared "enviar proposta por email" popup. Pre-fills the client email,
 * subject and a default message; the proposal PDF is attached automatically.
 *
 * Flow on send:
 *   1. POST exportVerb  → (re)generates the PDF so the latest version is
 *      stored (pdf_url) and ready to attach.
 *   2. POST sendVerb { id, to, subject, body } → the host fetches the PDF
 *      from R2 and sends it via the tenant's email integration (the app
 *      never holds SMTP/R2 credentials).
 *
 * Verbs are injected so the same dialog serves solar (solar_export_pdf /
 * solar_send_email) and AC/BC (export_pdf / send_email).
 */

import { useEffect, useState } from "react"
import { Loader2, Mail, Paperclip, Sparkles } from "lucide-react"
import type { AppShell } from "@/App"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { proposalFileBase } from "@/lib/format"

export function EmailDialog({
  shell,
  open,
  onOpenChange,
  proposalId,
  number,
  clientName,
  clientEmail,
  exportVerb,
  sendVerb,
  onSent,
}: {
  shell: AppShell | null
  open: boolean
  onOpenChange: (v: boolean) => void
  proposalId: string
  number: string
  clientName: string
  clientEmail: string | null
  /** Action verb that (re)generates the PDF, e.g. "export_pdf" / "solar_export_pdf". */
  exportVerb: string
  /** Action verb that sends the email, e.g. "send_email" / "solar_send_email". */
  sendVerb: string
  onSent?: () => void
}) {
  const [to, setTo] = useState("")
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [step, setStep] = useState<"idle" | "pdf" | "sending">("idle")
  const [drafting, setDrafting] = useState(false)

  // Texto do email no formato da Mais Energia (draft_email): saudação, dois
  // parágrafos da IA adaptados à proposta, o que inclui (✅) e o texto fixo das
  // Configurações. Falhando, fica o texto curto de sempre.
  async function draft() {
    if (!shell) return
    setDrafting(true)
    try {
      const res = await shell.client.post<{ success: boolean; data?: { subject: string; body: string }; error?: string }>(
        "actions/draft_email",
        { params: { id: proposalId } },
      )
      if (res.success && res.data) {
        setSubject(res.data.subject)
        setBody(res.data.body)
      }
    } catch {
      // fica o texto por omissão
    } finally {
      setDrafting(false)
    }
  }

  // Reset/prefill whenever the dialog opens.
  useEffect(() => {
    if (!open) return
    setTo(clientEmail ?? "")
    setSubject(`Proposta ${number}`)
    setBody(
      `Olá ${clientName || ""},\n\n` +
        `Em anexo a nossa proposta ${number}.\n` +
        `Ficamos disponíveis para qualquer esclarecimento.\n\n` +
        `Com os melhores cumprimentos,`,
    )
    setStep("idle")
    void draft()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientEmail, clientName, number])

  const busy = step !== "idle" || drafting
  const canSend = to.trim().length > 3 && to.includes("@") && !busy

  async function send() {
    if (!shell || !canSend) return
    try {
      // 1. (re)generate the PDF so the latest version is attached.
      setStep("pdf")
      const exp = await shell.client.post<{ success: boolean; error?: string }>(
        `actions/${exportVerb}`,
        { params: { id: proposalId } },
      )
      if (!exp.success) throw new Error(exp.error || "Falha a gerar o PDF")

      // 2. send with the PDF attached (host fetches it from R2).
      setStep("sending")
      const res = await shell.client.post<{ success: boolean; message?: string; error?: string }>(
        `actions/${sendVerb}`,
        { params: { id: proposalId, to: to.trim(), subject: subject.trim(), body } },
      )
      if (!res.success) throw new Error(res.error || "Falha a enviar")

      shell.bridge.toast({ variant: "success", description: res.message ?? `Email enviado a ${to.trim()}` })
      onOpenChange(false)
      onSent?.()
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha a enviar email",
      })
    } finally {
      setStep("idle")
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (busy ? null : onOpenChange(v))}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-accent" /> Enviar proposta por email
          </DialogTitle>
          <DialogDescription>
            O PDF da proposta vai em anexo. O envio usa o email configurado no CRM.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Para</Label>
            <Input
              type="email"
              placeholder="cliente@email.pt"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="space-y-1">
            <Label>Assunto</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={busy} />
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label>Mensagem</Label>
              <Button type="button" variant="ghost" size="sm" onClick={() => void draft()} disabled={busy}>
                {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {drafting ? "A preparar o texto…" : "Gerar de novo"}
              </Button>
            </div>
            <textarea
              className="flex min-h-[320px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={busy}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            No fim do email entra a assinatura de email do comercial da proposta (a da conta no Koortex), e o cliente responde-lhe diretamente.
          </p>
          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            <Paperclip className="h-4 w-4" />
            {proposalFileBase(number, clientName)}.pdf
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={send} disabled={!canSend}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            {step === "pdf" ? "A gerar PDF…" : step === "sending" ? "A enviar…" : "Enviar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
